"use client";
import { useState, useEffect } from "react";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { useRouter, useSearchParams } from "next/navigation";
import { ColumnDef } from "@tanstack/react-table";
import { fetchData } from "@/utils/api-handler";
import { DEFAULT_TABLE_PAGE_LIMIT, DEFAULT_TABLE_SORT_BY_CREATED_AT, SEARCH_DEBOUNCE_WAIT } from "@/utils/global";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { PermissionGate } from "@/components/shared/permission-gate";
import { AccessDeniedCard } from "@/components/shared/access-denied-card";
import {
    ArrowUpDown,
    MoreHorizontal,
    Search,
    Server,
    Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DeleteConfirmationDialog } from "@/components/shared/delete-dialog";
import { DataTable } from "@/components/shared/data-table";
import { Loader } from "@/components/ui/loader";
import { getPageRange } from "@/utils/global";
import { CreateDataSourceDialog } from "@/components/documents/dialogs/create-datasource-dialog";
import { IDCell } from "@/components/shared/id-cell";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { getDatasourceColumns } from "@/components/documents/tables/datasource-columns";
import TablePagination from "@/components/shared/table-pagination";



interface DatasourceListProps {
    setActiveSection: (section: string) => void;
    setSelectedDataSource: (ds: DataSource) => void;
}

export function DatasourceList({ setActiveSection, setSelectedDataSource }: DatasourceListProps) {
    const router = useRouter();
    const searchParams = useSearchParams();

    const [currentPage, setCurrentPage] = useState(() => parseInt(searchParams.get("page") || "1", 10) || 1);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: SEARCH_DEBOUNCE_WAIT });
    const [sortBy, setSortBy] = useState<string>(DEFAULT_TABLE_SORT_BY_CREATED_AT.sortBy);
    const [sortOrder, setSortOrder] = useState<string>(DEFAULT_TABLE_SORT_BY_CREATED_AT.sortOrder);

    const handleSort = (column: string) => {
        setSortBy(column);
        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    };

    const { data: datasourceData, isLoading, hasPermission } = useProtectedQuery<DataSourceListResponse>({
        queryKey: ["datasources", currentPage, debouncedSearch, sortBy, sortOrder],
        queryFn: async () => {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                page_size: DEFAULT_TABLE_PAGE_LIMIT.toString(),
                sort_by: sortBy,
                sort_order: sortOrder,
            });
            if (debouncedSearch) {
                params.append("search", debouncedSearch);
            }
            return fetchData(`/api/documents/datasources?${params.toString()}`);
        },
        requiredPermissions: ["LIST_DATA_SOURCES"],
    });

    const columns = getDatasourceColumns({ setActiveSection, setSelectedDataSource, sortBy, sortOrder, handleSort });

    useEffect(() => {
        if (datasourceData?.page_count && currentPage > datasourceData.page_count) {
            setCurrentPage(1);
            const params = new URLSearchParams(searchParams.toString());
            params.set("page", "1");
            router.replace(`${window.location.pathname}?${params.toString()}`);
        }
    }, [datasourceData?.page_count, currentPage, router, searchParams]);

    return (
        <>
            {!hasPermission && (
                <AccessDeniedCard message="You don't have permission to list data sources. Contact your administrator if you need access." />
            )}
            {hasPermission && (
                <>
                    <div className="mb-5 mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="relative w-full md:max-w-[280px] md:shrink-0">
                            <Input
                                className="w-full pl-8"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                startIcon={
                                    <Search className="h-4 w-4 tex absolute text-muted-foreground left top-1/2 transform -translate-y-1/2" />
                                }
                            />
                        </div>
                        <div className="w-full md:w-auto [&_button]:w-full md:[&_button]:w-auto">
                            <PermissionGate requiredPermissions={["CREATE_DATA_SOURCE"]}>
                                <CreateDataSourceDialog />
                            </PermissionGate>
                        </div>
                    </div>
                    <div className="relative overflow-hidden border rounded-lg">
                        {isLoading && <Loader />}
                        <div className="overflow-auto max-h-[calc(80vh-189px)]">
                            
                                <DataTable data={datasourceData?.datasources ?? []} columns={columns} />
                        </div>
                    </div>
                    <TablePagination
                        currentPage={currentPage}
                        pageCount={datasourceData?.page_count || 0}
                        onPageChange={setCurrentPage}
                        pageParamName="page"
                    />
                </>
            )}
        </>
    );
}
